import React, { useEffect, useState } from 'react';
import MarsMap from '../../../components/common/map-components/map/Map';
import { Markers } from '../../../types/map';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import {
  MyWatchList,
  MyWatchListFleetVessel,
} from '../../../types/my-watch-list';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import LiveTrackingInfoWindow from '../../../components/vessel-info/live-tracking-info-window/LiveTrackingInfoWindow';
import countryList from '../../../components/Country.json';
import { useTranslation } from 'react-i18next';
import { LatLngExpression } from 'leaflet';
import { useLocation, useSearchParams } from 'react-router-dom';
import toast from '../../../utils/toast';

export default function ViewVesselLiveTracking() {
  const { setIsLoading } = useLoaderContext();
  const { t } = useTranslation();
  const [markers, setMarkers] = useState<Markers[]>([]);
  const [showNoDataFound, setShowNoDataFound] = useState<boolean>(false);
  const [center, setCenter] = useState<LatLngExpression>([0, 0]);
  const [allVessels, setAllVessels] = useState<MyWatchListFleetVessel[]>([]);
  const [fleetList, setFleetList] = useState<any[]>([]);
  const [selectedFleetIds, setSelectedFleetIds] = useState<number[]>([]);
  const [searchedVessels, setSearchedVessels] = useState<any[]>([]);
  const [fetchingImo, setFetchingImo] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const selectedImo = searchParams.get('selectedImo');
  const searchTrigger = searchParams.get('t');
  const location = useLocation();

  useEffect(() => {
    getMyWatchListGadget();
  }, []);

  useEffect(() => {
    if (allVessels.length > 0 || searchedVessels.length > 0) {
      updateMarkersForSelectedImo();
    }
  }, [
    selectedImo,
    allVessels,
    searchTrigger,
    selectedFleetIds,
    searchedVessels,
  ]);

  const getMyWatchListGadget = async () => {
    await api
      .get({ url: apiConfig.myWatchListGadget }, setIsLoading)
      .then((data: MyWatchList) => {
        if (data && data.fleetVesselList?.length) {
          setAllVessels(data.fleetVesselList);
          setFleetList(data.fleetList || []);
          setSelectedFleetIds(
            data.fleetList?.map((fleet) => fleet.fleetId) || []
          );
          setSearchedVessels((prev) =>
            prev.filter(
              (searchedVessel) =>
                !data.fleetVesselList.some(
                  (watchlistVessel) =>
                    watchlistVessel.vesselDetails?.imoNumber ===
                    searchedVessel.imo
                )
            )
          );
          setShowNoDataFound(false);
          setMarkers([]);
        } else {
          setAllVessels([]);
          setFleetList([]);
          setSelectedFleetIds([]);
          setShowNoDataFound(true);

          if (selectedImo && fetchingImo !== selectedImo) {
            setFetchingImo(selectedImo);
            getLiveTrackingDataForImo(selectedImo, markers);
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching watchlist:', error);
        setAllVessels([]);
        setFleetList([]);
        setSelectedFleetIds([]);
        setShowNoDataFound(true);
      });
  };

  const getLiveTrackingDataForImo = async (
    imo: string,
    existingMarkers: Markers[] = []
  ) => {
    const params = { imo: imo };

    await api
      .get({ url: apiConfig.liveTracking, params }, setIsLoading)
      .then((res) => {
        if (res && res.lat && res.lon) {
          const iso3 = location.state?.iso3;
          const isInWatchlist = allVessels.some(
            (vessel) => vessel.vesselDetails?.imoNumber === res.imo
          );

          if (!isInWatchlist) {
            setSearchedVessels((prev) => {
              if (!prev.some((v) => v.imo === res.imo)) {
                return [
                  ...prev,
                  {
                    imo: res.imo,
                    vesselName: res.name,
                    lat: res.lat,
                    lon: res.lon,
                    heading: res.heading || 0,
                    iso3,
                    vesselType: res.type || '',
                    selected: true,
                    vesselPosition: res,
                  },
                ];
              }
              return prev;
            });
          }

          setCenter([res.lat, res.lon]);
          setShowNoDataFound(false);
          setFetchingImo(null);
        } else {
          toast.error({
            title: t('toast.noDataFound'),
            message: t('toast.noDataFoundForVessel', {
              vesselImo: selectedImo,
            }),
          });
          setShowNoDataFound(true);
          setFetchingImo(null);
        }
      })
      .catch((error) => {
        console.error('Error fetching live tracking data:', error);
        setShowNoDataFound(true);
        setFetchingImo(null);
      });
  };

  const updateMarkersForSelectedImo = () => {
    let foundSelectedVessel = false;
    const mainMarkers: Markers[] = allVessels
      .filter(
        (v: MyWatchListFleetVessel) =>
          (selectedFleetIds.includes(v.fleetId) ||
            v.vesselDetails?.imoNumber === selectedImo) &&
          v.vesselLiveDataDetails?.lat &&
          v.vesselLiveDataDetails?.lon
      )
      .map((vessel: MyWatchListFleetVessel) => {
        const isSelected =
          selectedImo && vessel.vesselDetails?.imoNumber === selectedImo;
        if (isSelected) {
          foundSelectedVessel = true;
        }

        return {
          iconName: 'arrowSymbol',
          iconRotation: vessel.vesselLiveDataDetails?.heading || 0,
          coordinates: {
            lat: vessel.vesselLiveDataDetails?.lat || 0,
            lng: vessel.vesselLiveDataDetails?.lon || 0,
          },
          markerColor: vessel.fleetColor,
          imo: vessel.vesselDetails?.imoNumber,
          isSelected: !!isSelected,
          infoWindowContent: (
            <LiveTrackingInfoWindow
              position={vessel.vesselLiveDataDetails}
              vesselDetails={vessel.vesselDetails}
              flag={
                countryList.find(
                  (countryItem) =>
                    countryItem.iso_3 === vessel.vesselDetails?.iso3
                )?.flag || ''
              }
              t={t as any}
              showAddtoFleetPortCallButtons={true}
              isFromLiveTrackingApi={false}
              refreshWatchList={getMyWatchListGadget}
            />
          ),
        };
      });

    const searchedMarkers: Markers[] = searchedVessels
      .filter((vessel) => {
        return (
          vessel.selected &&
          !allVessels.some(
            (watchlistVessel) =>
              watchlistVessel.vesselDetails?.imoNumber === vessel.imo
          )
        );
      })
      .map((vessel) => {
        const isSelected = selectedImo && vessel.imo === selectedImo;
        const iso3 = vessel.iso3;

        const vesselDetails: any = {
          vesselName: vessel.vesselName,
          imoDisplayName: vessel.imo,
          imoNumber: vessel.imo,
          iso3: iso3 || '',
          vesselType: vessel.vesselType || '',
        };

        const flagUrl =
          countryList.find((countryItem) => countryItem.iso_3 === iso3)?.flag ||
          '';

        return {
          iconName: 'arrowSymbol',
          iconRotation: vessel.heading || 0,
          coordinates: {
            lat: vessel.lat,
            lng: vessel.lon,
          },
          isSelected: !!isSelected,
          infoWindowContent: (
            <LiveTrackingInfoWindow
              position={vessel.vesselPosition}
              vesselDetails={vesselDetails}
              flag={flagUrl}
              t={t as any}
              showAddtoFleetPortCallButtons={true}
              isFromLiveTrackingApi={true}
              refreshWatchList={getMyWatchListGadget}
            />
          ),
        };
      });

    const allMarkers = [...mainMarkers, ...searchedMarkers];
    setMarkers([]);
    setMarkers(allMarkers);

    if (selectedImo && !foundSelectedVessel) {
      const searchedVessel = searchedVessels.find((v) => v.imo === selectedImo);
      if (!searchedVessel && fetchingImo !== selectedImo) {
        setFetchingImo(selectedImo);
        getLiveTrackingDataForImo(selectedImo, allMarkers);
      }
    }

    if (selectedImo && foundSelectedVessel) {
      const selectedVessel = allVessels.find(
        (v: MyWatchListFleetVessel) =>
          v.vesselDetails?.imoNumber === selectedImo
      );
      if (selectedVessel) {
        setCenter([
          selectedVessel.vesselLiveDataDetails?.lat || 0,
          selectedVessel.vesselLiveDataDetails?.lon || 0,
        ]);
      }
    } else if (selectedImo) {
      const searchedVessel = searchedVessels.find((v) => v.imo === selectedImo);
      if (searchedVessel) {
        setCenter([searchedVessel.lat, searchedVessel.lon]);
      }
    } else if (!selectedImo) {
      const firstValidVessel = allVessels.find(
        (v: MyWatchListFleetVessel) =>
          selectedFleetIds.includes(v.fleetId) &&
          v.vesselLiveDataDetails?.lat &&
          v.vesselLiveDataDetails?.lon
      );
      if (firstValidVessel) {
        setCenter([
          firstValidVessel.vesselLiveDataDetails?.lat || 0,
          firstValidVessel.vesselLiveDataDetails?.lon || 0,
        ]);
      } else {
        setCenter([0, 0]);
      }
    }
  };

  const handleVesselSelectionChange = (selectedVesselImos: string[]) => {
    setSearchedVessels((prev) =>
      prev.map((vessel) => ({
        ...vessel,
        selected: selectedVesselImos.includes(vessel.imo),
      }))
    );
  };

  return (
    <div className="inner-wrapper page-inner-wrap">
      <MarsMap
        markers={markers}
        zoom={4}
        center={center}
        selectedImo={selectedImo}
        isMeasuringToolVisible={true}
        isVesselFilterVisible={true}
        isDistanceCalculatorVisible={true}
        enableVesselHighlight={true}
        fleetList={fleetList}
        onFleetSelectionChange={setSelectedFleetIds}
        searchedVessels={searchedVessels}
        onVesselSelectionChange={handleVesselSelectionChange}
        transformOrigin={[12, 12]}
      />
    </div>
  );
}
